import React, { useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import {
  StyledForm,
  FormGroup,
  UpgradeText,
  ChannelFormSection,
  FieldHead,
  UpgradeButtonLink,
  UpgradeButtonLinkContainer,
} from "./styledComponents";
import Button from "components/Button";
import { ColorPickerButton } from "components/InputField/File";
import { ChannelInputField } from "./components";
import Divider from "components/Divider";
import { Controller, useWatch } from "react-hook-form";
import {
  containsUrlLike,
  isRestrictedTier,
  URL_BLOCK_MESSAGE,
} from "utils/textMessageUrlScreener";
import UpgradeCta from "containers/UpgradeCta";

// Per-field map of tiers that can both *edit* the field AND should be
// screened for URL-like content. The Rails service screens both fields
// for both restricted tiers. In this React form LITE never sees either
// input and PLUS edits both. Screening a field a tier
// can't edit silently disables the Save button with no visible error
// (Greptile #231 finding). Keep this aligned with the per-field
// `eligibleTiers` declared on each option below.
const URL_SCREENED_FIELDS_BY_TIER = {
  lite: [],
  plus: ["phone_copy", "email_copy"],
};

export const MessagingForm = ({
  edit,
  submitting,
  openColorPickerModal,
  messageForm,
  onSubmit,
  permissionRole,
  nextTab,
  form,
  brandForm,
}) => {
  // Get form values to access channel name and gallery settings
  const values = useWatch({ control: form.control });
  const channelName = values?.name || "your event";

  // Anti-spam guard: screen the message-body fields for URL-like content
  // when the owner is on a restricted tier. The Rails service is the
  // source of truth — this client check is defense in depth, scoped to
  // the fields the current tier can actually edit so we never disable
  // Save based on a field the user has no way to fix.
  const tierIsRestricted = isRestrictedTier(permissionRole);
  const screenedFields = useMemo(
    () =>
      URL_SCREENED_FIELDS_BY_TIER[
        String(permissionRole || "").toLowerCase()
      ] || [],
    [permissionRole],
  );
  const urlScreenErrors = useMemo(() => {
    if (!tierIsRestricted) return {};
    return screenedFields.reduce((acc, field) => {
      if (containsUrlLike(values?.[field])) acc[field] = URL_BLOCK_MESSAGE;
      return acc;
    }, {});
  }, [tierIsRestricted, screenedFields, values]);
  const hasUrlBlockError = Object.keys(urlScreenErrors).length > 0;

  // Get gallery values for defaults (from brandForm or form values)
  const galleryHeadline = brandForm?.header || values?.header || "";
  const gallerySubHeadline = brandForm?.sub_header || values?.sub_header || "";
  const galleryCTAText = brandForm?.gallery_cta_text || values?.gallery_cta_text || "";
  const galleryCTALink = brandForm?.gallery_cta_link || values?.gallery_cta_link || "";

  // Track if we've already prefilled the fields to avoid overwriting user input
  const prefillAppliedRef = useRef({
    email_heading: false,
    email_copy: false,
    email_cta_text: false,
    email_cta_link: false,
  });

  // Prefill messaging fields with gallery values when gallery values are set and messaging fields are empty
  useEffect(() => {
    // Prefill email_heading with gallery headline if headline is set and email_heading is empty
    if (galleryHeadline && !values?.email_heading && !prefillAppliedRef.current.email_heading) {
      form.setValue("email_heading", galleryHeadline);
      prefillAppliedRef.current.email_heading = true;
    }
    // Prefill email_copy with gallery sub-headline if sub-headline is set and email_copy is empty
    if (gallerySubHeadline && !values?.email_copy && !prefillAppliedRef.current.email_copy) {
      form.setValue("email_copy", gallerySubHeadline);
      prefillAppliedRef.current.email_copy = true;
    }
    // Prefill email_cta_text with gallery CTA text if CTA text is set and email_cta_text is empty
    if (galleryCTAText && !values?.email_cta_text && !prefillAppliedRef.current.email_cta_text) {
      form.setValue("email_cta_text", galleryCTAText);
      prefillAppliedRef.current.email_cta_text = true;
    }
    // Prefill email_cta_link with gallery CTA link if CTA link is set and email_cta_link is empty
    if (galleryCTALink && !values?.email_cta_link && !prefillAppliedRef.current.email_cta_link) {
      form.setValue("email_cta_link", galleryCTALink);
      prefillAppliedRef.current.email_cta_link = true;
    }
  }, [galleryHeadline, gallerySubHeadline, galleryCTAText, galleryCTALink, values, form]);

  // Template for email/text subject with channel name
  const channelNameTemplate = `📸 Your ${channelName} photo is here!`;
  const formOptions = [
    {
      header: "Email Messaging",
      description:
        "Customize the messaging you’d like guests to see when they receive their images by email.",
      label: "Email Messaging",
      eligibleTiers: ["plus", "pro", "super_admin", "enterprise_admin"],
      upgradeButton: (
        <div style={{ marginTop: "20px" }}>
          <UpgradeButtonLinkContainer>
            <UpgradeCta as={UpgradeButtonLink}>
              Upgrade to set email subject, body copy, and call-to-action
            </UpgradeCta>
          </UpgradeButtonLinkContainer>
        </div>
      ),
      subFields: [
        {
          name: "email_subject_line",
          label: "Email Subject (Required)",
          placeholder: channelNameTemplate,
          defaultValue: channelNameTemplate,
          type: "text",
          eligibleTiers: ["plus", "pro", "super_admin", "enterprise_admin"],
        },
        {
          name: "email_heading",
          label: "Email Heading",
          placeholder: "Thank you for visiting our photo booth!",
          defaultValue: galleryHeadline,
          type: "text",
          eligibleTiers: ["plus", "pro", "super_admin", "enterprise_admin"],
        },
        {
          name: "email_copy",
          label: "Email Body",
          placeholder: "We hope you enjoy your photos.",
          defaultValue: gallerySubHeadline,
          type: "text",
          eligibleTiers: ["plus", "pro", "super_admin", "enterprise_admin"],
          multiline: 5,
        },
        {
          name: "email_cta_text",
          label: "CTA Button Text",
          placeholder: "Get 10% Off Your Next Purchase",
          defaultValue: galleryCTAText,
          type: "text",
          eligibleTiers: ["plus", "pro", "super_admin", "enterprise_admin"],
        },
        {
          name: "email_cta_link",
          label: "CTA Button URL",
          placeholder: "https://www.movebooth.com/",
          defaultValue: galleryCTALink,
          type: "text",
          eligibleTiers: ["plus", "pro", "super_admin", "enterprise_admin"],
          upgradeButton: null,
        },
      ],
    },
    {
      header: "Text Messaging",
      description:
        "Customize the messaging you'd like guests to see when they receive their images by text message.",
      label: "Text Messaging",
      eligibleTiers: ["plus", "pro", "super_admin", "enterprise_admin"],
      upgradeButton: (
        <div style={{ marginTop: "20px" }}>
          <UpgradeButtonLinkContainer>
            <UpgradeCta as={UpgradeButtonLink}>
              Upgrade to set text message copy
            </UpgradeCta>
          </UpgradeButtonLinkContainer>
        </div>
      ),
      subFields: [
        {
          name: "phone_copy",
          label: "Text Message (Required)",
          eligibleTiers: ["pro", "plus", "super_admin", "enterprise_admin"],
          placeholder: channelNameTemplate,
          defaultValue: channelNameTemplate,
          type: "text",
          upgradeButton: (
            <div style={{ marginTop: "20px" }}>
              <UpgradeButtonLinkContainer>
                <UpgradeCta as={UpgradeButtonLink}>
                  Upgrade to add URL link support to text messages
                </UpgradeCta>
              </UpgradeButtonLinkContainer>
            </div>
          ),
          multiline: 5,
        },
      ],
    },
    // Twitter Messaging section removed - Twitter share feature is no longer functional
    // {
    //   header: "Twitter Messaging",
    //   description:
    //     "Customize the messaging you'd like guests to see when receive their images by Twitter.",
    //   label: "Twitter Messaging",
    //   eligibleTiers: ["plus", "pro", "super_admin", "enterprise_admin"],
    //   upgradeButton: (
    //     <div style={{ marginTop: "20px" }}>
    //       <UpgradeButtonLinkContainer>
    //         <UpgradeButtonLink
    //           margin="20px 0px"
    //           href="mailto:sales@movebooth.com?&subject=Movebooth%20Account%20Upgrade%20Request&body=I'd like to upgrade to set Twitter copy!"
    //           target="_blank"
    //           rel="noreferrer noopener"
    //         >
    //           Request to upgrade to set Twitter copy
    //         </UpgradeButtonLink>
    //       </UpgradeButtonLinkContainer>
    //     </div>
    //   ),
    //   subFields: [
    //     {
    //       name: "twitter_copy",
    //       label: "tweet copy",
    //       eligibleTiers: ["pro", "plus", "super_admin", "enterprise_admin"],
    //       placeholder:
    //         permissionRole === "pro"
    //           ? "📸 Your Hooli selfie from Movebooth is here! Get 10% off your next purchase at https://www.yourstore.com/movebooth"
    //           : "📸 Your Hooli selfie from Movebooth is here!",
    //
    //       type: "text",
    //       multiline: 5,
    //       upgradeButton: (
    //         <div style={{ marginTop: "20px" }}>
    //           <UpgradeButtonLinkContainer>
    //             <UpgradeButtonLink
    //               href="mailto:sales@movebooth.com?&subject=Movebooth%20Account%20Upgrade%20Request&body=I'd Like to upgrade to add URL link support to Tweets!"
    //               margin="0px 0px 20px 0px"
    //               target="_blank"
    //               rel="noreferrer noopener"
    //             >
    //               Request to upgrade to add URL link support to Tweets
    //             </UpgradeButtonLink>
    //           </UpgradeButtonLinkContainer>
    //         </div>
    //       ),
    //     },
    //   ],
    // },
  ];
  const { control, handleSubmit } = form;

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      {formOptions.map((options, idx) => {
        const {
          header,
          description,
          type,
          subFields,
          eligibleTiers,
          upgradeButton,
        } = options;
        return (
          <FormGroup key={idx}>
            <ChannelFormSection>
              {(header || description) && type !== "upgrade" && (
                <FieldHead className="brand">
                  <strong>{header}</strong>
                  <h3 className="customize-the-message">{description}</h3>
                </FieldHead>
              )}
              {eligibleTiers.includes(permissionRole) ? (
                subFields &&
                subFields
                  .filter((x) => x)
                  .map((options, idx) => {
                    if (
                      options.eligibleTiers &&
                      options.eligibleTiers.includes(permissionRole)
                    ) {
                      switch (options.type) {
                        case "color":
                          const value =
                            messageForm[options.name] || options.defaultColor;
                          return (
                            <div key={idx} className="field-file">
                              <Controller
                                control={control}
                                name={options.name}
                                render={({ field: { ref, ...fieldRest } }) => (
                                  <ColorPickerButton
                                    name={messageForm[options.name]}
                                    onClick={openColorPickerModal}
                                    buttonText={value}
                                    backgroundColor={value}
                                    field={fieldRest}
                                    {...fieldRest}
                                    {...options}
                                  />
                                )}
                              />
                            </div>
                          );
                        default:
                          return (
                            <div key={idx} className="field-text">
                              <Controller
                                control={control}
                                name={options.name}
                                render={({ field: { ref, ...fieldRest } }) => (
                                  <ChannelInputField
                                    name={messageForm[options.name]}
                                    field={fieldRest}
                                    {...fieldRest}
                                    {...options}
                                    errorMsg={
                                      urlScreenErrors[options.name] ||
                                      options.errorMsg
                                    }
                                  />
                                )}
                              />
                              {urlScreenErrors[options.name] && (
                                <div
                                  role="alert"
                                  className="text-sm text-red-600 mt-1"
                                  data-testid={`url-block-error-${options.name}`}
                                >
                                  {urlScreenErrors[options.name]}
                                </div>
                              )}
                              {permissionRole === "plus" &&
                                options.upgradeButton}
                            </div>
                          );
                      }
                    } else return <div key={idx}>{options.upgradeButton}</div>;
                  })
              ) : (
                <div key={idx}>{upgradeButton}</div>
              )}
            </ChannelFormSection>
          </FormGroup>
        );
      })}
      <Divider marginBottom={48}></Divider>
      <UpgradeText className="left-pos pdd-20">
        Looking for more? Send us a suggestion.
      </UpgradeText>
      <div className="channel-forms__group">
        {edit ? (
          <Button type="submit" disabled={submitting || hasUrlBlockError}>
            Save
          </Button>
        ) : (
          <Button
            type="button"
            disabled={hasUrlBlockError}
            onClick={() => nextTab()}
          >
            Save & Continue
          </Button>
        )}
      </div>
    </StyledForm>
  );
};

export default MessagingForm;
